import * as Yup from 'yup'
import { Col, InputGroup, Form, Row } from 'react-bootstrap'
import { camelCase, partition } from 'lodash'
import { modifySettingDetail } from '../'
import { getColumnString, CustomForm } from '../../common'
import { HeadingCombinationSettingType, Orientation } from '../../common/types'

type HeadingCombinationSetting = {
  id: number
  columns: number
  headingCombinationSettingType: string
  headingType: string
  name: string
}

type EnumValues = {
  headingCombinationSettingTypes: Record<string, string>
  headingTypes: Record<string, string>
}

type SettingProp = {
  id: number
  headingCombinationSettings?: HeadingCombinationSetting[] | null
}

type SingleItemValues = {
  id: number
  columns: number
  headingCombinationSettingType: string
  headingType: string
  name: string
}

const getOrientation = (combinationType: string) => {
  switch (camelCase(combinationType)) {
    case HeadingCombinationSettingType.HHeadingSmallCenter:
    case HeadingCombinationSettingType.HHeadingSmallTop:
    case HeadingCombinationSettingType.HHeadingMediumCenter:
    case HeadingCombinationSettingType.HHeadingMediumTop:
    case HeadingCombinationSettingType.HHeadingLargeCenter:
    case HeadingCombinationSettingType.HHeadingLargeTop:
    case HeadingCombinationSettingType.HHeadingHalfCenter:
    case HeadingCombinationSettingType.HHeadingHalfTop:
      return Orientation.Horizontal
    default:
      return Orientation.Vertical
  }
}

const _HeadingCombinationSettingSingleItem = ({
  values,
  errors,
  touched,
  handleChange,
  handleSubmit,
  orientation,
  rowIndex,
  enumValues,
  isSettingWritable
}: {
  values: SingleItemValues
  errors: { name?: string }
  touched: { name?: boolean }
  handleChange: React.ChangeEventHandler<HTMLElement>
  handleSubmit: (values: SingleItemValues) => void
  orientation: string
  rowIndex: number
  enumValues: EnumValues
  isSettingWritable: boolean
}) => {
  const columnString = getColumnString(String(values.columns))
  const typeString = enumValues.headingCombinationSettingTypes[camelCase(values?.headingCombinationSettingType)]
  const headingTypeString = enumValues.headingTypes[camelCase(values?.headingType)]
  return (
    <Row sm='auto' className={`${orientation}-row-${rowIndex + 1}`}>
      <Col sm={2}>
        {columnString} {typeString} {headingTypeString}
      </Col>
      <Col sm={3}>
        <InputGroup size='sm' hasValidation>
          <Form.Control
            id={`nameInput-${values.id}`}
            type='text'
            name='name'
            value={values.name || ''}
            onBlur={(_) => handleSubmit(values)}
            onChange={handleChange}
            isInvalid={!!(touched.name && errors.name)}
            disabled={!isSettingWritable}
          />
          <Form.Control.Feedback type='invalid'>{errors?.name}</Form.Control.Feedback>
        </InputGroup>
      </Col>
    </Row>
  )
}

const HeadingCombinationSettingSingleItem = Object.assign(_HeadingCombinationSettingSingleItem, {
  schema: Yup.object().shape({
    name: Yup.string().trim().required()
  })
})

const _HeadingCombinationSettingForm = ({
  setting,
  enumValues,
  initialValues: _initialValues,
  values: _values,
  handleSubmit: _handleSubmit,
  handleChange: _handleChange,
  dispatch,
  ...props
}: {
  setting: SettingProp
  enumValues: EnumValues
  initialValues?: unknown
  values?: unknown
  handleSubmit?: unknown
  handleChange?: unknown
  dispatch: (action: unknown) => void
  [key: string]: unknown
}) => {
  const handleSubmit = ({ id, ...rest }: { id: number; [key: string]: unknown }) => {
    const snakeSettingName = 'heading_combination_settings'
    const headingCombinationSetting = { id, ...rest }
    dispatch(
      modifySettingDetail({
        settingId: setting.id,
        settingName: snakeSettingName,
        id,
        headingCombinationSetting
      })
    )
  }
  if (!setting?.headingCombinationSettings) {
    return <></>
  }

  const [verticalTypeSettings, horizontalTypeSettings] = partition(
    setting?.headingCombinationSettings || [],
    (s) => getOrientation(s.headingCombinationSettingType) === Orientation.Vertical
  )
  return (
    <>
      <div className='h4'>縦見出し</div>
      {verticalTypeSettings.map((row, rowIndex) => {
        const orientation = Orientation.Vertical
        const formSetting = {
          initialValues: { ...row },
          Component: HeadingCombinationSettingSingleItem,
          validationSchema: HeadingCombinationSettingSingleItem.schema,
          handleSubmit,
          props: { rowIndex, orientation, setting, enumValues, ...props }
        }
        const componentKey = `${orientation}-${row.columns}-${row.headingCombinationSettingType}-${row.headingType}`
        return <CustomForm key={componentKey} form={formSetting} />
      })}
      <div className='h4 mt-2'>横見出し</div>
      {horizontalTypeSettings.map((row, rowIndex) => {
        const orientation = Orientation.Horizontal
        const formSetting = {
          initialValues: { ...row },
          Component: HeadingCombinationSettingSingleItem,
          validationSchema: HeadingCombinationSettingSingleItem.schema,
          handleSubmit,
          props: { rowIndex, orientation, setting, enumValues, ...props }
        }
        const componentKey = `${orientation}-${row.columns}-${row.headingCombinationSettingType}-${row.headingType}`
        return <CustomForm key={componentKey} form={formSetting} />
      })}
    </>
  )
}

export const HeadingCombinationSettingForm = Object.assign(_HeadingCombinationSettingForm, {
  schema: Yup.object().shape({})
})
